import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import useProfile from "@/hooks/useProfile";
import { Wordmark } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, Building2, Link2, ListChecks, ScrollText,
  BarChart3, HeartPulse, FileText, SlidersHorizontal, ShieldQuestion, Users, ShieldCheck, LogOut, Menu, X, Settings, Scale, Sun, Moon, Plug, Sparkles, Radio
} from "lucide-react";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  client_admin: "Client Admin",
  client_viewer: "Viewer",
};

function navGroups(role) {
  const overview = [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }];
  if (role === "super_admin") overview.push({ to: "/clients", label: "Clients", icon: Building2 });

  const moderation = [
    { to: "/feed", label: "Live Feed", icon: Radio },
    { to: "/accounts", label: "Connected Accounts", icon: Link2 },
    { to: "/review", label: "Review Queue", icon: ListChecks },
    { to: "/passed", label: "Passed Comments", icon: ShieldQuestion },
    { to: "/log", label: "Removal Log", icon: ScrollText },
  ];
  const insight = [
    { to: "/brand", label: "Brand Health", icon: HeartPulse },
    { to: "/posts", label: "Post Reports", icon: FileText },
    { to: "/content", label: "Content Studio", icon: Sparkles },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/rules", label: "Rules", icon: SlidersHorizontal },
  ];
  const admin = [];
  if (role !== "client_viewer") admin.push({ to: "/team", label: "Team", icon: Users });
  admin.push(
    { to: "/audit", label: "Audit Log", icon: ShieldCheck },
    { to: "/compliance", label: "Compliance", icon: Scale },
  );
  if (role !== "client_viewer") admin.push(
    { to: "/integrations", label: "Integrations", icon: Plug },
    { to: "/settings", label: "AI Settings", icon: Settings },
  );

  return [
    { title: null, items: overview },
    { title: "Moderation", items: moderation },
    { title: "Insight", items: insight },
    { title: "Administration", items: admin },
  ];
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const profile = useProfile();
  const { theme, setTheme } = useTheme();

  const groups = navGroups(user?.role);
  const initials = (user?.name || user?.email || "U")
    .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const showBell = user && user.role !== "super_admin";
  const bell = showBell ? <NotificationBell isAdmin={user.role === "client_admin"} /> : null;
  const isDark = theme !== "light";
  const themeToggle = (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="shrink-0 p-2 rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/[0.06] transition-colors"
    >
      {isDark ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
    </button>
  );

  const SidebarContent = () => (
    <>
      <div className="shrink-0 flex items-center justify-between gap-3 px-2 pb-6" data-testid="brand-logo">
          <div className="leading-none">
            <p className="flex items-center gap-2 font-heading text-[19px] font-semibold text-sidebar-foreground tracking-[0.14em]">
              <span className="sr-only">COMO</span>
              <Wordmark className="h-6 w-auto text-sidebar-foreground" aria-hidden="true" />
              {profile && profile !== "real" && (
                <span
                  data-testid="profile-pill"
                  title={profile === "demo" ? "Sample data for presenting" : "Test database"}
                  className="font-sans text-[9px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border border-sidebar-primary/50 text-sidebar-primary"
                >
                  {profile === "demo" ? "Demo" : "Test"}
                </span>
              )}
            </p>
            <p className="text-[10px] text-sidebar-muted tracking-[0.12em] uppercase mt-1.5 whitespace-nowrap">Bloom Dev Labs</p>
          </div>
          <div className="flex items-center">{bell}{themeToggle}</div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <nav aria-label="Main" className="space-y-6">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.title && <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-muted/70">{g.title}</p>}
              <div className="space-y-0.5">
                {g.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 pl-3 pr-3 py-2 rounded-md text-[13px] transition-colors ${
                        isActive
                          ? "bg-sidebar-accent/[0.07] text-sidebar-foreground font-medium before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:rounded-full before:bg-sidebar-primary"
                          : "text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/[0.04]"
                      }`
                    }
                  >
                    <item.icon className="w-4 h-4 shrink-0 opacity-80" strokeWidth={1.5} />
                    <span className="flex-1">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      <div className="shrink-0 border-t border-sidebar-border/10 pt-3">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-lg border border-sidebar-border/20 text-sidebar-foreground flex items-center justify-center text-[11px] font-semibold tracking-wide shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-sidebar-foreground truncate">{user?.name}</p>
            <p className="text-[11px] text-sidebar-muted truncate">
              {ROLE_LABEL[user?.role]}
              {user?.client?.name ? ` · ${user.client.name}` : ""}
            </p>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/[0.04] transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-dvh bg-background flex relative">
      <a href="#main" className="skip-link">Skip to content</a>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-sidebar border-r border-sidebar-border/[0.06] flex-col px-4 py-6 sticky top-0 h-dvh overflow-hidden z-10">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-sidebar flex flex-col h-dvh px-4 py-6 overflow-hidden animate-fade-in-up">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 h-14 bg-sidebar border-b border-sidebar-border/[0.06] sticky top-0 z-30">
          <div className="flex items-center gap-2.5">
            <Wordmark className="h-5 w-auto text-sidebar-foreground" />
            {bell}{themeToggle}
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} className="p-3 -mr-2 text-sidebar-foreground">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <main id="main" className="flex-1 overflow-y-auto px-4 py-6 sm:px-8 lg:px-12 lg:py-10 relative z-[1]"><div className="max-w-[1280px] mx-auto">{children}</div></main>
      </div>
    </div>
  );
}
